import React from "react";
import { Box, Button, Checkbox, Flex, FlexProps, IconButton, Text, Tooltip } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import { ChakraFAIcon } from "@/components/ui/Icon";
import { deriveActiveTileMinHeightPx, tileScaleFromCardWidth } from "./seatLayout";
import { floatingToolbarBg } from "./FloatingToolbar";
import { MOVE_HERE_INDIGO, ghostSeatDndId, seatDndId } from "./seatDnd";
import { tileContentHeightPxFromScale } from "@/stores/v2/classroomLayoutStore";
import InactiveStudentWindow from "@/js/components/StudentTile/components/InactiveStudentWindow";
import { ClassNotStarted } from "@/js/components/StudentTile/components/inactiveScreenStates/ClassNotStarted";
import { default as IconSignOut } from "assets/IconSignOut.svg";

const noop = (): void => {};

export interface SeatStudent {
	qoriaUserId: string;
	stableIdV1: string;
	username: string;
	firstName: string;
	lastName: string;
	pinned?: boolean;
}

interface SeatProps {
	index: number;
	student: SeatStudent | null;
	cardWidth: number;
	selected: boolean;
	/** When false, this seat is not a drag source (pinned strip). */
	draggable?: boolean;
	selectable?: boolean;
	/** Keeps the checkbox visible but inert, e.g. while an arrangement is unsaved. */
	selectionDisabled?: boolean;
	/** When true, show the release-claim control in the corner Flex (left of the checkbox). */
	releasable?: boolean;
	cards?: JSX.Element[];
	onToggleSelect: (qoriaUserId: string) => void;
	onReleaseClaim?: (qoriaUserId: string) => void;
	isOver?: boolean;
	isOriginHole?: boolean;
	pendingMove?: boolean;
	tabIndex?: number;
	onActivate?: (index: number) => void;
	hasActionRow?: boolean;
	isClassActive?: boolean;
	/** Ghost drop target rendered below the last row while dragging. */
	ghost?: boolean;
	/** When false, the seat is not a drop target (pinned strip). */
	droppable?: boolean;
}

const focusVisibleStyles = {
	outline: "2px solid",
	outlineColor: floatingToolbarBg,
	outlineOffset: "2px",
};

/** Height of the grab strip along the top of a draggable seat tile. */
export const SEAT_GRIP_BAR_HEIGHT_PX = 14;

/** Diameter of the circular bubble holding the pin toggle in the tile's top-left corner. */
const SEAT_PIN_BUBBLE_PX = 26;

/** Offset of the pin bubble from the card header. Half of the circle sits above / left of the tile. */
const SEAT_PIN_BUBBLE_TOP_PX = -22;
const SEAT_PIN_BUBBLE_LEFT_PX = -10;

/** The pin glyph only carries a `viewBox`, so it needs an explicit size inside the bubble. */
const SEAT_PIN_ICON_WIDTH_PX = 12;
const SEAT_PIN_ICON_HEIGHT_PX = 15;

/**
 * The card header pins its own controls to the top right, which in a seat is where the checkbox
 * and swap controls live. Give the pin a round bubble on the tile's top-left corner instead.
 *
 * The bubble is positioned by the card header. Without a grab strip (pinned tiles) it overlaps
 * the name row, which then needs clearing.
 */
export function seatPinBubbleSx(hasGripBar: boolean): Record<string, unknown> {
	return {
		// The default view clips the card at the smallest tile size (`.StudentTile_grid_small
		// .StudentTile`), which would slice the overhanging bubble off at the card edge. Doubled
		// selector so this outweighs that class pair.
		"&& .StudentTile": { overflow: "visible" },
		// Theme tokens are not resolved inside nested selectors, so these are literal CSS values.
		".Pin_button:not(.release_claim_button)": {
			top: `${SEAT_PIN_BUBBLE_TOP_PX}px`,
			left: `${SEAT_PIN_BUBBLE_LEFT_PX}px`,
			right: "auto",
			width: `${SEAT_PIN_BUBBLE_PX}px`,
			height: `${SEAT_PIN_BUBBLE_PX}px`,
			padding: 0,
			borderRadius: "9999px",
			background: "#ffffff",
			boxShadow: "0 1px 4px rgba(5, 24, 57, 0.25)",
			display: "grid",
			placeItems: "center",
		},
		".Pin_button:not(.release_claim_button):hover": { background: "#f7f7f7" },
		".Pin_button:not(.release_claim_button) svg": {
			width: `${SEAT_PIN_ICON_WIDTH_PX}px`,
			height: `${SEAT_PIN_ICON_HEIGHT_PX}px`,
		},
		...(hasGripBar ? {} : { ".StudentTileHeader_title": { paddingLeft: `${SEAT_PIN_BUBBLE_PX}px` } }),
	};
}

interface SeatGripBarProps extends FlexProps {
	testId?: string;
}

/**
 * Full-width grab strip at the top of a draggable tile. It sits in normal flow so it reads as
 * tile chrome rather than an icon competing with the student name. It is the tile's only drag
 * source, so interacting with the card below never moves the seat.
 */
export const SeatGripBar = React.forwardRef<HTMLDivElement, SeatGripBarProps>(function SeatGripBar({ testId, ...handleProps }, ref) {
	return (
		<Flex
			ref={ref}
			data-testid={testId}
			data-seat-grip=""
			flex="0 0 auto"
			width="100%"
			height={`${SEAT_GRIP_BAR_HEIGHT_PX}px`}
			align="center"
			justify="center"
			bg="gray.50"
			// The tile no longer clips its children (the pin bubble overhangs the top edge), so the
			// strip rounds its own top corners to sit inside the tile's radius.
			borderTopRadius="md"
			borderBottom="1px solid"
			borderColor="gray.100"
			color="gray.400"
			transition="color 0.1s ease, background 0.1s ease"
			aria-hidden="true"
			{...handleProps}
		>
			<ChakraFAIcon icon="fa-grip-dots" variant="solid" fontSize="10px" />
		</Flex>
	);
});

function MoveHereOverlay({ testId }: { testId: string }): JSX.Element {
	const { t } = useTranslation();
	return (
		<Flex
			data-testid={testId}
			position="absolute"
			inset="0"
			border="2px dashed"
			borderColor={MOVE_HERE_INDIGO}
			borderRadius="md"
			align="flex-end"
			justify="center"
			pointerEvents="none"
			zIndex={4}
			pb={2}
			bg="rgba(79, 70, 229, 0.08)"
		>
			<Text color={MOVE_HERE_INDIGO} fontSize="xs" fontWeight="semibold">
				{t("seatPlan.moveHere")}
			</Text>
		</Flex>
	);
}

function SeatComponent(props: SeatProps): JSX.Element {
	const {
		index,
		student,
		cardWidth,
		selected,
		draggable: draggableProp = true,
		selectable = true,
		selectionDisabled = false,
		releasable = false,
		cards,
		onToggleSelect,
		onReleaseClaim,
		isOver = false,
		isOriginHole = false,
		pendingMove = false,
		tabIndex = -1,
		onActivate,
		hasActionRow = true,
		isClassActive = false,
		ghost = false,
		droppable: droppableProp = true,
	} = props;
	const { t } = useTranslation();
	const minHeight = deriveActiveTileMinHeightPx(cardWidth, hasActionRow);
	const contentHeightPx = tileContentHeightPxFromScale(tileScaleFromCardWidth(cardWidth));
	const tileSizingStyle: React.CSSProperties = {
		["--tile-content-height-px" as string]: `${contentHeightPx}px`,
	};
	const [activeCard, setActiveCard] = React.useState(0);
	const [gripHovered, setGripHovered] = React.useState(false);
	// A drag unmounts the grip before it can fire mouseleave, so clear the hover once the seat changes.
	React.useEffect(() => setGripHovered(false), [student?.qoriaUserId, isOriginHole]);

	const showAsEmpty = !student || isOriginHole || ghost;
	const canDrag = !!student && draggableProp && !isOriginHole && !ghost;
	const dndId = ghost ? ghostSeatDndId(index) : seatDndId(index);
	const { attributes, listeners, setNodeRef: setDragRef, setActivatorNodeRef } = useDraggable({ id: dndId, disabled: !canDrag });
	const { setNodeRef: setDropRef } = useDroppable({ id: dndId, disabled: !droppableProp });

	const setRefs = (node: HTMLDivElement | null) => {
		setDragRef(node);
		setDropRef(node);
	};

	const highlight = isOver && !isOriginHole;
	const seatNumber = index + 1;
	const studentName = student ? `${student.firstName} ${student.lastName}` : null;

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (!onActivate) return;
		// Ignore keys bubbling from inside the tile (e.g. typing a space in a chat or announcement field).
		if (e.target !== e.currentTarget) return;
		if (e.key === "Enter" || e.key === " ") {
			e.preventDefault();
			onActivate(index);
		}
	};

	const moveA11yProps = onActivate
		? {
				role: "button" as const,
				tabIndex,
				"aria-roledescription": t("seatPlan.movableSeat"),
				"aria-label": studentName
					? t("seatPlan.seatLabel", { name: studentName, n: seatNumber })
					: t("seatPlan.emptySeatLabel", { n: seatNumber }),
				"aria-pressed": student ? pendingMove : undefined,
				"aria-describedby": canDrag ? attributes["aria-describedby"] : undefined,
				onKeyDown: handleKeyDown,
				_focusVisible: focusVisibleStyles,
			}
		: {};

	// Lift the tile only while its grab strip is hovered, since that is the only drag source.
	const gripHoverStyles =
		canDrag && !pendingMove && gripHovered
			? {
					"data-grip-hovered": "true",
					transform: "translateY(-2px)",
					boxShadow: "lg",
					zIndex: 10,
					// Theme tokens are not resolved inside nested selectors (gray.600 / gray.100).
					sx: { "& [data-seat-grip]": { color: "#737373", background: "#f0f0f0" } },
				}
			: {};

	const pickedUpStyles =
		pendingMove && !isOriginHole
			? {
					outline: "2px solid",
					outlineColor: floatingToolbarBg,
					outlineOffset: "2px",
					transform: "translateY(-4px)",
					boxShadow: "2xl",
					zIndex: 10,
				}
			: {};

	if (showAsEmpty) {
		return (
			<Flex
				ref={setRefs}
				data-testid={ghost ? `seatplan-ghost-${index}` : `seatplan-seat-${index}`}
				data-seat-empty=""
				data-drop-active={highlight ? "true" : undefined}
				position="relative"
				width={`${cardWidth}px`}
				minHeight={`${minHeight}px`}
				border="2px dashed"
				borderColor={highlight ? MOVE_HERE_INDIGO : "gray.400"}
				bg={highlight ? "rgba(79, 70, 229, 0.08)" : "transparent"}
				borderRadius="md"
				align="center"
				justify="center"
				direction="column"
				gap={1}
				px={2}
				opacity={ghost && !highlight ? 0.55 : 1}
				cursor="default"
				{...moveA11yProps}
				{...pickedUpStyles}
			>
				<Text color="gray.600" fontSize="sm" fontWeight="medium" textAlign="center" aria-hidden="true">
					{t("seatPlan.emptySeat")}
				</Text>
				<Text color="gray.600" fontSize="xs" textAlign="center" aria-hidden="true">
					{highlight ? t("seatPlan.moveHere") : t("seatPlan.emptySeatDragHint")}
				</Text>
			</Flex>
		);
	}

	const hasCards = !!cards && cards.length > 0;
	const hasMultipleCards = isClassActive && !!cards && cards.length > 1;
	const activeIndex = hasCards ? activeCard % cards.length : 0;
	const showSelectedOutline = selected;
	const showOutline = highlight || showSelectedOutline;

	return (
		<Box
			ref={setRefs}
			data-testid={`seatplan-seat-${index}`}
			data-seat-student={selectable ? student.qoriaUserId : undefined}
			data-drop-active={highlight ? "true" : undefined}
			data-selected={showSelectedOutline ? "true" : undefined}
			position="relative"
			width={`${cardWidth}px`}
			minHeight={`${minHeight}px`}
			borderRadius="md"
			// Visible so the pin bubble can sit half outside the tile's top edge. The grab strip and
			// the injected card round their own corners to stay inside the tile's radius.
			overflow="visible"
			bg="white"
			boxShadow="sm"
			outline={showOutline ? "2px solid" : "none"}
			outlineColor={showOutline ? (highlight ? MOVE_HERE_INDIGO : floatingToolbarBg) : "transparent"}
			outlineOffset={showOutline ? "2px" : undefined}
			transition="transform 0.1s ease, box-shadow 0.1s ease"
			{...gripHoverStyles}
			style={tileSizingStyle}
			display="flex"
			flexDirection="column"
			{...(canDrag ? attributes : {})}
			{...moveA11yProps}
			{...pickedUpStyles}
		>
			{canDrag && (
				<SeatGripBar
					ref={setActivatorNodeRef}
					testId={`seatplan-seat-${index}-drag-handle`}
					cursor="grab"
					onMouseEnter={() => setGripHovered(true)}
					onMouseLeave={() => setGripHovered(false)}
					{...listeners}
				/>
			)}
			<Flex
				data-testid={`seatplan-seat-${index}-corner-controls`}
				position="absolute"
				// The grab strip occupies the top of the tile, so the corner controls start below it.
				top={canDrag ? `${SEAT_GRIP_BAR_HEIGHT_PX + 6}px` : "6px"}
				right="6px"
				zIndex={3}
				align="center"
				gap="4px"
				cursor="default"
				onPointerDown={(e) => e.stopPropagation()}
				onClick={(e) => e.stopPropagation()}
			>
				{hasMultipleCards && (
					<Button
						data-testid={`seatplan-seat-${index}-swap-screen`}
						aria-label={t("seatPlan.swapScreen")}
						size="xs"
						variant="ghost"
						py={2}
						transform="translateY(-2px)"
						leftIcon={<ChakraFAIcon icon="fa-right-left" variant="solid" />}
						onClick={(e) => {
							e.stopPropagation();
							setActiveCard((current) => (current + 1) % (cards as JSX.Element[]).length);
						}}
					>
						{t("seatPlan.screenCounter", { current: activeIndex + 1, total: cards?.length ?? 0 })}
					</Button>
				)}
				{releasable && (
					<Tooltip label={t("studentTile.releaseClaim")} placement="top" hasArrow>
						<IconButton
							data-testid={`seatplan-seat-${index}-release-claim`}
							aria-label={t("studentTile.releaseClaim")}
							size="xs"
							variant="ghost"
							bg="white"
							transform="translateY(-2px)"
							icon={<IconSignOut />}
							onClick={(e) => {
								e.stopPropagation();
								onReleaseClaim?.(student.qoriaUserId);
							}}
						/>
					</Tooltip>
				)}
				{selectable && (
					<Box bg="white" borderRadius="sm" p="1px">
						<Checkbox
							id={`seatplan-seat-${index}-checkbox`}
							data-testid={`seatplan-seat-${index}-checkbox`}
							isChecked={selected}
							isDisabled={selectionDisabled}
							onChange={() => onToggleSelect(student.qoriaUserId)}
							sx={{
								".chakra-checkbox__control[data-checked]": {
									borderColor: floatingToolbarBg,
									_before: { background: floatingToolbarBg },
								},
							}}
						/>
					</Box>
				)}
			</Flex>

			{hasCards ? (
				<Box
					data-testid={`seatplan-seat-${index}-card-slot`}
					width="100%"
					flex="1 1 auto"
					minH={0}
					display="flex"
					flexDirection="column"
					sx={{
						".StudentTile": {
							width: "100%",
							boxShadow: "none",
							margin: 0,
							flex: "1 1 auto",
							minHeight: 0,
						},
						".Pin_button.release_claim_button": { display: "none" },
						...seatPinBubbleSx(canDrag),
					}}
				>
					{cards[activeIndex]}
				</Box>
			) : isClassActive ? (
				<Box
					data-testid={`seatplan-seat-${index}-connecting`}
					width="100%"
					flex="1 1 auto"
					minH={0}
					display="flex"
					flexDirection="column"
					aria-busy="true"
				>
					<InactiveStudentWindow
						connecting
						screenshotRetryUsed={false}
						issueOngoingUsed={false}
						useScreenshotRetry={noop}
						useIssueOngoing={noop}
					/>
				</Box>
			) : (
				<Box
					data-testid={`seatplan-seat-${index}-class-not-started`}
					width="100%"
					flex="1 1 auto"
					minH={0}
					display="flex"
					flexDirection="column"
				>
					<ClassNotStarted />
				</Box>
			)}
			{highlight ? <MoveHereOverlay testId={`seatplan-move-here-${index}`} /> : null}
		</Box>
	);
}

export const Seat = React.memo(SeatComponent);
